import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, Timer, Trash2 } from 'lucide-react';
import { MAX_TIME_ENTRY_MINUTES, type RunningTimer } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Card } from './Card.tsx';
import { bucketLabel, derivedBucketForTicketType } from '../timer/buckets.ts';
import { formatDuration } from '../timer/useTimer.ts';

/**
 * Every timer running right now, across the team, with the two operations an
 * admin or time admin needs on someone else's clock: log it with a corrected
 * duration, or discard it.
 *
 * This exists because timers are otherwise self-scoped — a clock left running
 * by someone who is out of office had no owner able to clear it, and past 24h
 * it could not be committed at all, which blocked that agent from starting any
 * new timer. Both writes are audited.
 */
export function RunningTimersPanel(): React.ReactElement | null {
  const [timers, setTimers] = useState<RunningTimer[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch<{ timers: RunningTimer[] }>('/api/timer/active');
      setTimers(res.timers);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load running timers.');
    }
  }, []);

  useEffect(() => {
    void load();
    const i = setInterval(() => void load(), 60_000);
    return () => clearInterval(i);
  }, [load]);

  // Nothing running and nothing to say — stay out of the way.
  if (!error && timers !== null && timers.length === 0) return null;

  const runaway = (timers ?? []).filter((t) => t.isRunaway).length;

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--color-header)]">
          <Timer className="h-4 w-4 text-[var(--color-t3-blue)]" />
          Running timers
          {timers && <span className="text-sm font-normal text-[var(--color-muted)]">({timers.length})</span>}
        </h2>
        {runaway > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-900">
            <AlertTriangle className="h-3.5 w-3.5" />
            {runaway} left running — needs a real duration
          </span>
        )}
      </div>

      {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}

      {timers === null ? (
        <p className="text-sm text-[var(--color-muted)]">Loading…</p>
      ) : (
        <div className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
              <tr>
                <th className="px-4 py-2">Agent</th>
                <th className="px-4 py-2">Working on</th>
                <th className="px-4 py-2">Started</th>
                <th className="px-4 py-2 text-right">Running</th>
                <th className="px-4 py-2 text-right">Log / discard</th>
              </tr>
            </thead>
            <tbody>
              {timers.map((t) => (
                <TimerRow key={t.agentId} timer={t} onDone={load} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function roundToQuarter(minutes: number): string {
  const hours = Math.round((minutes / 60) * 4) / 4;
  return hours >= 0.25 ? String(hours) : '0.25';
}

function TimerRow({
  timer,
  onDone,
}: {
  timer: RunningTimer;
  onDone: () => Promise<void>;
}): React.ReactElement {
  // A sane clock pre-fills with what it actually ran; a runaway one starts
  // blank, because its elapsed time is exactly the number not to trust.
  const [hours, setHours] = useState(() =>
    timer.isRunaway ? '' : roundToQuarter(timer.elapsedMinutes),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsed = Number(hours);
  const minutes = Number.isFinite(parsed) ? Math.round(parsed * 60) : NaN;
  const valid = hours.trim() !== '' && Number.isFinite(minutes) && minutes >= 1 && minutes <= MAX_TIME_ENTRY_MINUTES;

  async function run(fn: () => Promise<void>, fallback: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
    } finally {
      setBusy(false);
    }
  }

  const bucket = timer.billingBucket ?? derivedBucketForTicketType(timer.ticketType);

  return (
    <tr className={'border-t border-[var(--color-border)] ' + (timer.isRunaway ? 'bg-amber-50/60' : '')}>
      <td className="px-4 py-2 font-medium text-[var(--color-header)]">{timer.agentName}</td>
      <td className="px-4 py-2">
        {timer.ticketId ? (
          <Link to={`/tickets/${timer.ticketId}`} className="text-[var(--color-t3-blue)] hover:underline">
            T-{timer.ticketDisplayNumber} {timer.ticketSubject}
          </Link>
        ) : (
          <span>
            {timer.organizationName ?? 'Internal'}
            {bucket ? ` · ${bucketLabel(bucket)}` : ''}
          </span>
        )}
      </td>
      <td className="px-4 py-2 text-[var(--color-muted)]">
        {new Date(timer.startedAt).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })}
      </td>
      <td
        className={
          'px-4 py-2 text-right font-mono tabular-nums ' +
          (timer.isRunaway ? 'font-semibold text-amber-900' : 'text-[var(--color-body)]')
        }
      >
        {formatDuration(timer.elapsedMinutes)}
      </td>
      <td className="px-4 py-2">
        <div className="flex items-center justify-end gap-2">
          <input
            type="number"
            min="0.25"
            max={MAX_TIME_ENTRY_MINUTES / 60}
            step="0.25"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            placeholder="hours"
            aria-label={`Hours to log for ${timer.agentName}`}
            className="w-20 rounded-[var(--radius-button)] border border-[var(--color-border)] px-2 py-1 text-sm"
          />
          <button
            type="button"
            disabled={busy || !valid}
            onClick={() =>
              void run(
                () =>
                  apiFetch(`/api/timer/active/${timer.agentId}/stop`, {
                    method: 'POST',
                    json: { durationMinutes: minutes },
                  }),
                'Failed to stop that timer.',
              )
            }
            className="rounded-[var(--radius-button)] bg-[var(--color-t3-blue)] px-3 py-1 text-xs font-semibold text-white transition hover:brightness-95 disabled:bg-[#cccccc] disabled:text-[#888888]"
          >
            {busy ? '…' : 'Stop & log'}
          </button>
          <button
            type="button"
            title="Discard without logging time"
            disabled={busy}
            onClick={() => {
              if (
                !confirm(
                  `Discard ${timer.agentName}'s timer without logging any time?`,
                )
              ) {
                return;
              }
              void run(
                () => apiFetch(`/api/timer/active/${timer.agentId}`, { method: 'DELETE' }),
                'Failed to discard that timer.',
              );
            }}
            className="rounded-[var(--radius-button)] border border-[var(--color-border)] px-2 py-1 text-[var(--color-muted)] transition hover:border-[var(--color-danger)] hover:text-[var(--color-danger)]"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
        {error && <p className="mt-1 text-right text-xs text-[var(--color-danger)]">{error}</p>}
      </td>
    </tr>
  );
}
